import isFunction from 'lodash/isFunction';

import { getGa } from 'core/googleAnalytics';

import { PriceChangeFormValues } from './bulk-actions/components/BulkPriceChange/BulkPriceChangeModal';

type EventCategory = 'MENU';

export type BulkActionEventActions =
  | 'BULK_ADD_MANDATORY_WARNINGS'
  | 'BULK_ADD_OPTIONS'
  | 'BULK_CHANGE_AVAILABILITY_VISIBILITY'
  | 'BULK_CHANGE_COMMISSIONS'
  | 'BULK_CHANGE_ITEM_CATEGORY'
  | 'BULK_CHANGE_PRICES'
  | 'BULK_CHANGE_VAT'
  | 'BULK_CREATE_WOLT_SPECIALS'
  | 'BULK_DELETE_ITEMS'
  | 'BULK_DISABLE_ITEMS'
  | 'BULK_ENABLE_ITEMS'
  | 'BULK_SET_COURIER_RESTRICTION';

type EventAction =
  | 'ADD_CATEGORY'
  | 'ADD_ITEM'
  | 'ADD_OPTION'
  | BulkActionEventActions
  | 'CANCEL_ITEM_EDITING'
  | 'CANCEL_OPTION_EDITING'
  | 'CHANGE_ITEM_CATEGORY'
  | 'COPY_ITEM'
  | 'CREATE_MENU'
  | 'DELETE_CATEGORY'
  | 'DELETE_ITEM'
  | 'DELETE_OPTION'
  | 'DELETE_OPTION_CHOICE'
  | 'DISABLE_CATEGORY_ITEMS'
  | 'DISABLE_ITEM'
  | 'DISABLE_OPTION_CHOICE'
  | 'DOWNLOAD_ITEMS_AS_CSV'
  | 'EDIT_CATEGORY'
  | 'EDIT_ITEM'
  | 'EDIT_OPTION'
  | 'ENABLE_CATEGORY_ITEMS'
  | 'ENABLE_ITEM'
  | 'ENABLE_OPTION_CHOICE'
  | 'EXPORT_CATEGORY_ITEMS'
  | 'EXPORT_MENU'
  | 'EXPORT_MENU_WITH_DATETIME'
  | 'EXPORT_ITEMS_AS_CSV'
  | 'EXPORT_PRICES_AS_CSV'
  | 'IMPORT_CATEGORY_ITEMS'
  | 'IMPORT_MENU'
  | 'IMPORT_PRICES_FROM_CSV'
  | MenuSyncEventActions
  | 'UPDATE_ITEMS_FROM_CSV'
  | 'VIEW_ON_WOLTCOM';

export type MenuSyncEventActions = 'INITIATE_SYNC_ACTION' | 'OPEN_SYNC_ACTION' | 'OPEN_SYNC_HISTORY';

type ReportPayload = {
  hitType: 'event';
  eventCategory: EventCategory;
  eventAction: EventAction;
  eventLabel?: string;
  eventValue?: number;
  dimension4?: number;
  dimension5?: number;
  dimension6?: boolean;
  dimension7?: boolean;
};

const report = (payload: ReportPayload) => {
  const ga = getGa();

  if (!isFunction(ga)) {
    return;
  }

  ga('send', payload);
};

export const reportMenuSyncActionInitiate = (
  label: 'start-now' | 'start-scheduled' | 'cancel',
  selectedCount: number,
  totalAvailableCount: number,
  daysScheduledInAdvance?: number | undefined,
  keepAvailabilityInfo?: boolean,
  keepPriceInfo?: boolean,
) => {
  report({
    hitType: 'event',
    eventCategory: 'MENU',
    eventAction: 'INITIATE_SYNC_ACTION',
    eventLabel: label,
    eventValue: selectedCount,
    dimension4: totalAvailableCount,
    dimension5: label === 'start-scheduled' ? daysScheduledInAdvance : undefined,
    dimension6: !!keepPriceInfo,
    dimension7: !!keepAvailabilityInfo,
  });
};

export const reportMenuSyncActionOpen = (label: 'start' | 'cancel') => {
  report({
    hitType: 'event',
    eventCategory: 'MENU',
    eventAction: 'OPEN_SYNC_ACTION',
    eventLabel: label,
  });
};

export const reportMenuSyncHistoryOpen = (label: 'push' | 'pull') => {
  report({
    hitType: 'event',
    eventCategory: 'MENU',
    eventAction: 'OPEN_SYNC_HISTORY',
    eventLabel: label,
  });
};

export const reportWoltcomLinkClick = () => {
  report({
    hitType: 'event',
    eventCategory: 'MENU',
    eventAction: 'VIEW_ON_WOLTCOM',
  });
};

export const reportCategoryAdd = (categoryCount: number) => {
  report({
    hitType: 'event',
    eventCategory: 'MENU',
    eventAction: 'ADD_CATEGORY',
    eventValue: categoryCount,
  });
};

export const reportCategoryEdit = () => {
  report({
    hitType: 'event',
    eventCategory: 'MENU',
    eventAction: 'EDIT_CATEGORY',
  });
};

export const reportCategoryDelete = () => {
  report({
    hitType: 'event',
    eventCategory: 'MENU',
    eventAction: 'DELETE_CATEGORY',
  });
};

export const reportItemAdd = () => {
  report({
    hitType: 'event',
    eventCategory: 'MENU',
    eventAction: 'ADD_ITEM',
  });
};

export const reportItemDelete = () => {
  report({
    hitType: 'event',
    eventCategory: 'MENU',
    eventAction: 'DELETE_ITEM',
  });
};

export const reportItemEnabledToggle = (enabled: boolean) => {
  report({
    hitType: 'event',
    eventCategory: 'MENU',
    eventAction: enabled ? 'ENABLE_ITEM' : 'DISABLE_ITEM',
  });
};

export const reportMenuExport = () => {
  report({
    hitType: 'event',
    eventCategory: 'MENU',
    eventAction: 'EXPORT_MENU',
  });
};

export const reportMenuExportWithDatetime = () => {
  report({
    hitType: 'event',
    eventCategory: 'MENU',
    eventAction: 'EXPORT_MENU_WITH_DATETIME',
  });
};

export const reportMenuImport = (keepPriceInfo: boolean, keepAvailabilityInfo: boolean) => {
  report({
    hitType: 'event',
    eventCategory: 'MENU',
    eventAction: 'IMPORT_MENU',
    eventLabel: 'fingerprinting',
    dimension6: keepPriceInfo,
    dimension7: keepAvailabilityInfo,
  });
};

export const reportDownloadItemsAsCSV = () => {
  report({
    hitType: 'event',
    eventCategory: 'MENU',
    eventAction: 'DOWNLOAD_ITEMS_AS_CSV',
  });
};

export const reportItemEdit = () => {
  report({
    hitType: 'event',
    eventCategory: 'MENU',
    eventAction: 'EDIT_ITEM',
  });
};

export const reportItemEditCancel = () => {
  report({
    hitType: 'event',
    eventCategory: 'MENU',
    eventAction: 'CANCEL_ITEM_EDITING',
  });
};

export const reportItemMakeCopy = () => {
  report({
    hitType: 'event',
    eventCategory: 'MENU',
    eventAction: 'COPY_ITEM',
  });
};

export const reportCreateMenu = () => {
  report({
    hitType: 'event',
    eventCategory: 'MENU',
    eventAction: 'CREATE_MENU',
  });
};

export const reportOptionAdd = () => {
  report({
    hitType: 'event',
    eventCategory: 'MENU',
    eventAction: 'ADD_OPTION',
  });
};

export const reportOptionEdit = () => {
  report({
    hitType: 'event',
    eventCategory: 'MENU',
    eventAction: 'EDIT_OPTION',
  });
};

export const reportOptionEditCancel = () => {
  report({
    hitType: 'event',
    eventCategory: 'MENU',
    eventAction: 'CANCEL_OPTION_EDITING',
  });
};

export const reportOptionDelete = () => {
  report({
    hitType: 'event',
    eventCategory: 'MENU',
    eventAction: 'DELETE_OPTION',
  });
};

export const reportCategoryItemsDisable = () => {
  report({
    hitType: 'event',
    eventCategory: 'MENU',
    eventAction: 'DISABLE_CATEGORY_ITEMS',
  });
};

export const reportCategoryItemsEnable = () => {
  report({
    hitType: 'event',
    eventCategory: 'MENU',
    eventAction: 'ENABLE_CATEGORY_ITEMS',
  });
};

export const reportCategoryItemsImport = () => {
  report({
    hitType: 'event',
    eventCategory: 'MENU',
    eventAction: 'IMPORT_CATEGORY_ITEMS',
  });
};

export const reportCategoryItemsExport = () => {
  report({
    hitType: 'event',
    eventCategory: 'MENU',
    eventAction: 'EXPORT_CATEGORY_ITEMS',
  });
};

export const reportCsvPriceImport = () => {
  report({
    hitType: 'event',
    eventCategory: 'MENU',
    eventAction: 'IMPORT_PRICES_FROM_CSV',
  });
};

export const reportCsvPriceExport = () => {
  report({
    hitType: 'event',
    eventCategory: 'MENU',
    eventAction: 'EXPORT_PRICES_AS_CSV',
  });
};

export const reportCsvItemExport = () => {
  report({
    hitType: 'event',
    eventCategory: 'MENU',
    eventAction: 'EXPORT_ITEMS_AS_CSV',
  });
};

export const reportCsvItemUpdate = () => {
  report({
    hitType: 'event',
    eventCategory: 'MENU',
    eventAction: 'UPDATE_ITEMS_FROM_CSV',
  });
};

export const reportItemCategoryChange = (method: 'dropdown' | 'dragAndDrop' | 'itemDetailsForm') => {
  report({
    hitType: 'event',
    eventCategory: 'MENU',
    eventAction: 'CHANGE_ITEM_CATEGORY',
    eventLabel: method,
  });
};

export const reportOptionChoiceEnabledToggle = (enabled: boolean) => {
  report({
    hitType: 'event',
    eventCategory: 'MENU',
    eventAction: enabled ? 'ENABLE_OPTION_CHOICE' : 'DISABLE_OPTION_CHOICE',
  });
};

export const reportOptionChoiceDelete = () => {
  report({
    hitType: 'event',
    eventCategory: 'MENU',
    eventAction: 'DELETE_OPTION_CHOICE',
  });
};

type BulkActionEventDetails = {
  priceChangeType?: PriceChangeFormValues['type'];
  categoryChangeMethod?: 'dragAndDrop' | 'modal';
  courierRestrictionType?: 'disableBikeDelivery' | 'disableCarDelivery' | 'disableMotorcycleDelivery';
};

export const reportBulkAction = (
  itemCount: number,
  eventAction: BulkActionEventActions,
  eventDetails?: BulkActionEventDetails,
) => {
  report({
    hitType: 'event',
    eventCategory: 'MENU',
    eventAction,
    eventLabel: getBulkActionEventLabel(eventAction, eventDetails),
    eventValue: itemCount,
  });
};

const getBulkActionEventLabel = (eventAction: BulkActionEventActions, eventDetails?: BulkActionEventDetails) => {
  if (eventAction === 'BULK_CHANGE_PRICES' && eventDetails?.priceChangeType) {
    return eventDetails.priceChangeType;
  }

  if (eventAction === 'BULK_CHANGE_ITEM_CATEGORY' && eventDetails?.categoryChangeMethod) {
    return eventDetails.categoryChangeMethod;
  }

  if (eventAction === 'BULK_SET_COURIER_RESTRICTION' && eventDetails?.courierRestrictionType) {
    return eventDetails.courierRestrictionType;
  }

  return undefined;
};
